import React, { useMemo, useRef, useState, useEffect } from 'react';
import TableLayout from '../../molecules/TableLayout';
import Table from '../../molecules/Table';
import Field from '../Field';
import Form from '../../molecules/Form/Form';
import { useForm } from '../../molecules/Form';
import { useContextHook } from 'use-context-hook';
import { JobLeadsContext } from '../../../Context/jobLeadsContext.jsx';
import { MdKeyboardArrowDown } from 'react-icons/md';
import Button from '../../molecules/Button';
import { useNavigate, useParams } from 'react-router-dom';
import { StyledSingleLeads } from './LeadsDetails.styles';
import loadingIcon from '../../../assets/icon-loading.gif';
import dummyImg from '../../../assets/avatar-2.svg';
import { capitalize, formatDate, formatStatus, handleDebouncedSearch, handleSearchNow } from '../../../helpers/common.js';
import { AuthContext } from '../../../Context/authContext.jsx';
import { IoMdSettings } from 'react-icons/io';
import { IoAdd, IoTrashOutline, IoCheckboxOutline, IoCloseOutline } from 'react-icons/io5';
import { IoIosArrowDown } from 'react-icons/io';
import Toast from '../../molecules/Toast/index.jsx';
import linkedinService from '../../../services/linkedinService.js';
import jobService from '../../../services/jobService.js';
import usePersistedSearchQuery from '../../../hooks/usePersistedSearchQuery';
import campaignService from '../../../services/campaignService';
import iconMenu from '../../../assets/icon-menu-dots-vertical.svg';
import CenterModal from '../../molecules/Modal/CenterModal';
import AddToModalContent from './AddToModalContent';
import { FaFilter } from 'react-icons/fa6';
import Filters from './Filters.jsx';

const SingleJobLeadsDetails = () => {
    const navigate = useNavigate();
    const [form] = useForm();
    const { id } = useParams();
    const [quickActive, setQuickActive] = useState(false);
    const [tags, setTags] = useState([]);
    const quickRef = useRef(null);
    const filtersRef = useRef(null);
    const filtersSidebarRef = useRef(null);
    const debounceRef = useRef(null);
    const [showDropdown, setShowDropdown] = useState(null);
    const [addToCampaignModal, setAddToCampaignModal] = useState({ open: false, leadId: null });
    const [addToListModal, setAddToListModal] = useState({ open: false, leadId: null });
    const [campaigns, setCampaigns] = useState([]);
    const [lists, setLists] = useState([]);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [selectedList, setSelectedList] = useState(null);
    const [addToCampaignLoading, setAddToCampaignLoading] = useState(false);
    const [addToListLoading, setAddToListLoading] = useState(false);
    const [removeLoading, setRemoveLoading] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [openFilters, setOpenFilters] = useState(false);
    const DropdownRef = useRef(null);
    const bulkDropdownRef = useRef(null);

    // Selection state
    const [selectedLeads, setSelectedLeads] = useState([]);
    const [totalSelectedCount, setTotalSelectedCount] = useState(0);
    const [isSelectAllMode, setIsSelectAllMode] = useState(false);
    const [showBulkDropdown, setShowBulkDropdown] = useState(false);
    const [selectAllLoading, setSelectAllLoading] = useState(false);

    const { jobName } = useContextHook(JobLeadsContext, v => ({
        jobName: v.jobName,
    }));

    const [searchQuery, setSearchQuery] = usePersistedSearchQuery({
        id: id,
        page: 1,
        itemsPerPage: 10,
        searchText: '',
        getAll: false,
        status: [],
        contactedStatus: 'ALL',
        source: [],
        campaigns: [],
        tagsFilter: [],
        listsFilter: [],
        dateCreatedFilter: '',
        jobTitle: '',
        companyName: '',
        companyNames: [],
        language: '',
        languages: [],
        locations: [],
        location: '',
        industry: '',
        industries: [],
        headline: '',
        headlines: [],
        emailExists: 'ALL',
        linkedinProfileExists: 'ALL',
        openToWork: 'ALL',
        isHiring: 'ALL',
    });

    // Use context for job leads data
    const { leadsData, leadsLoading, updateSearchQuery } = useContextHook(JobLeadsContext, v => ({
        leadsData: v.leadsData,
        leadsLoading: v.leadsLoading,
        updateSearchQuery: v.updateSearchQuery,
    }));

    const { refetch } = useContextHook(AuthContext, v => ({
        refetch: v.refetch,
    }));

    useEffect(() => {
        if (searchQuery?.id) {
            updateSearchQuery(searchQuery);
        }
    }, [JSON.stringify(searchQuery), updateSearchQuery]);

    const leads_data = leadsData;
    const leads_loading = leadsLoading;

    // Selection handlers
    const handleSelectAll = async () => {
        const totalItems = leads_data?.data?.totalItems || 0;

        if (selectedLeads.length === totalItems && totalItems > 0) {
            setSelectedLeads([]);
            setTotalSelectedCount(0);
            setIsSelectAllMode(false);
            return;
        }

        if (selectAllLoading) return;

        setSelectAllLoading(true);
        try {
            const allLeadIds = await jobService.getAllJobLeads({
                ...searchQuery,
                getAll: true,
            });

            const leadIds = allLeadIds?.data?.ids || [];
            setSelectedLeads(leadIds);
            setTotalSelectedCount(leadIds.length);
            setIsSelectAllMode(true);
        } catch (error) {
            Toast({ type: 'error', message: error.message || 'Failed to select all leads' });
            setSelectedLeads([]);
            setTotalSelectedCount(0);
            setIsSelectAllMode(false);
        } finally {
            setSelectAllLoading(false);
        }
    };

    const handleSelectLead = (leadId, checked) => {
        setTotalSelectedCount(0);
        setIsSelectAllMode(false);
        if (checked) {
            setSelectedLeads(prev => [...prev, leadId]);
        } else {
            setSelectedLeads(prev => prev.filter(id => id !== leadId));
        }
    };

    const isAllSelected = useMemo(() => {
        const currentVisibleIds = leads_data?.data?.items?.map(item => item.id) || [];
        if (currentVisibleIds.length === 0) return false;

        const totalItems = leads_data?.data?.totalItems || 0;
        if (isSelectAllMode) return true;
        const allVisibleSelected = currentVisibleIds.every(id => selectedLeads.includes(id));
        const allItemsSelected = selectedLeads.length === totalItems && totalItems > 0;

        return allVisibleSelected && allItemsSelected;
    }, [selectedLeads, leads_data, isSelectAllMode]);

    const isIndeterminate = useMemo(() => {
        return selectedLeads.length > 0 && !isAllSelected;
    }, [selectedLeads, isAllSelected]);

    const visibleSelectedCount = useMemo(() => {
        if (isSelectAllMode) {
            return totalSelectedCount;
        }
        return selectedLeads.length;
    }, [selectedLeads, totalSelectedCount]);

    const handleUnselectAll = () => {
        setSelectedLeads([]);
        setTotalSelectedCount(0);
        setIsSelectAllMode(false);
        setShowBulkDropdown(false);
    };

    const handleSelectAllLeads = async () => {
        if (selectAllLoading) return;

        setSelectAllLoading(true);
        setShowBulkDropdown(false);

        try {
            const allLeadIds = await jobService.getAllJobLeads({
                ...searchQuery,
                getAll: true,
            });

            const leadIds = allLeadIds?.data?.ids || [];
            setSelectedLeads(leadIds);
            setTotalSelectedCount(leadIds.length);
            setIsSelectAllMode(true);
        } catch (error) {
            Toast({ type: 'error', message: error.message || 'Failed to select all leads' });
            setSelectedLeads([]);
            setTotalSelectedCount(0);
            setIsSelectAllMode(false);
        } finally {
            setSelectAllLoading(false);
        }
    };

    const handleBulkAddToCampaign = () => {
        setShowBulkDropdown(false);
        setAddToCampaignModal({ open: true, leadId: selectedLeads });
        setSelectedCampaign(null);
        setPreviewData(null);
        fetchCampaigns();
    };

    const handleSort = field => {
        setSearchQuery(prev => {
            const newDirection = prev.sortField === field && prev.sortDirection === 'asc' ? 'desc' : 'asc';
            return { ...prev, sortField: field, sortDirection: newDirection, page: 1 };
        });
    };

    const renderSortableHeader = (label, field) => {
        const isActive = searchQuery.sortField === field;
        const direction = searchQuery.sortDirection;

        return (
            <div className="sortable-header">
                {label}
            </div>
        );
    };

    const columnNames = [
        <div className="th-label-box">
            <div className="checkbox-wrapper">
                <Field
                    type="checkbox"
                    name="select_all"
                    noMargin
                    label={<></>}
                    value={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={handleSelectAll}
                />
                <div className="bulk-dropdown-wrapper" ref={bulkDropdownRef}>
                    <button
                        type="button"
                        className="bulk-dropdown-trigger"
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowBulkDropdown(prev => !prev);
                        }}>
                        {visibleSelectedCount > 0
                            ? `${visibleSelectedCount} Selected`
                            : ''}
                        <IoIosArrowDown size={16} />
                    </button>
                    {showBulkDropdown && (
                        <div className="bulk-dropdown-menu">
                            <ul className="bulk-dropdown-list">
                                <li>
                                    <button
                                        type="button"
                                        className="bulk-dropdown-item"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSelectAllLeads();
                                        }}
                                        disabled={selectAllLoading}
                                        style={{ opacity: selectAllLoading ? 0.6 : 1, cursor: selectAllLoading ? 'not-allowed' : 'pointer' }}>
                                        <IoCheckboxOutline size={18} />
                                        {selectAllLoading ? 'Selecting...' : 'Select All'}
                                    </button>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className="bulk-dropdown-item"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleUnselectAll();
                                        }}>
                                        <IoCloseOutline size={18} />
                                        Unselect All
                                    </button>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className="bulk-dropdown-item"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleBulkAddToCampaign();
                                        }}
                                        disabled={visibleSelectedCount === 0}
                                        style={{ opacity: visibleSelectedCount === 0 ? 0.5 : 1, cursor: visibleSelectedCount === 0 ? 'not-allowed' : 'pointer' }}>
                                        <IoAdd size={18} />
                                        Add to Campaign
                                    </button>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className="bulk-dropdown-item"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleBulkAddToList();
                                        }}
                                        disabled={visibleSelectedCount === 0}
                                        style={{ opacity: visibleSelectedCount === 0 ? 0.5 : 1, cursor: visibleSelectedCount === 0 ? 'not-allowed' : 'pointer' }}>
                                        <IoAdd size={18} />
                                        Add to List
                                    </button>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className="bulk-dropdown-item"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleBulkRemoveFromJob();
                                        }}
                                        disabled={visibleSelectedCount === 0 || removeLoading}
                                        style={{ opacity: (visibleSelectedCount === 0 || removeLoading) ? 0.5 : 1, cursor: (visibleSelectedCount === 0 || removeLoading) ? 'not-allowed' : 'pointer' }}>
                                        <IoTrashOutline size={18} />
                                        {removeLoading ? 'Removing...' : 'Remove from Job'}
                                    </button>
                                </li>
                            </ul>
                        </div>
                    )}
                </div>
            </div>
            <span className="column-label">Name</span>
        </div>,
        renderSortableHeader('Status', 'status'),
        renderSortableHeader('Created date', 'created_date'),
        'Company',
        'Position',
        'Email',
        'Action',
    ];

    const Status = data => {
        switch (data.status) {
            case 'UPLOADING':
                return (
                    <div className="badge uploading">
                        <img src={loadingIcon} alt="loading" width="15" height="15" />
                        &nbsp;
                        {formatStatus(data?.status)}
                    </div>
                );
            case 'NEW':
                return <div className="badge new">{formatStatus(data?.status)}</div>;
            case 'ENRICHING_EMAIL':
                return (
                    <div className="badge uploading">
                        {' '}
                        <img src={loadingIcon} alt="loading" width="15" height="15" />
                        &nbsp;
                        {formatStatus(data?.status)}
                    </div>
                );
            case 'EMAIL_ENRICHMENT_DONE':
                return <div className="badge done">{formatStatus(data?.status)}</div>;
            case 'ENRICHING_MESSAGE':
                return (
                    <div className="badge uploading">
                        <img src={loadingIcon} alt="loading" width="15" height="15" />
                        &nbsp;
                        {formatStatus(data?.status)}
                    </div>
                );
            case 'MESSAGE_ENRICHMENT_DONE':
                return <div className="badge done">{formatStatus(data?.status)}</div>;
            case 'IN_CAMPAIGN':
                return (
                    <div className="badge uploading">
                        {' '}
                        <img src={loadingIcon} alt="loading" width="15" height="15" />
                        &nbsp;
                        {formatStatus(data?.status)}
                    </div>
                );
            case 'CAMPAIGN_DONE':
                return <div className="badge done">{formatStatus(data?.status)}</div>;
            case 'REPLIED':
                return <div className="badge replied">{formatStatus(data?.status)}</div>;
            case 'UNSUBSCRIBED':
                return <div className="badge unsubscribed">{formatStatus(data?.status)}</div>;
            case 'BLOCKED':
                return <div className="badge blocked">{formatStatus(data?.status)}</div>;
            case 'BOUNCED':
                return <div className="badge bounced">{formatStatus(data?.status)}</div>;
            case 'OPENED_EMAIL':
                return <div className="badge opend">{formatStatus(data?.status)}</div>;
            case 'CLICKED_LINK':
                return <div className="badge clicked">{formatStatus(data?.status)}</div>;
            default:
                break;
        }
    };

    const toggleDropdown = (e, id) => {
        e.stopPropagation();
        e.preventDefault();
        setShowDropdown(prevId => (prevId === id ? null : id));
    };

    const ActionBtn = data => (
        <ul className="action-list">
            <li className={`action-item ${showDropdown === data?.id ? 'active' : ''}`}>
                <button className="btn" type="button" onClick={e => toggleDropdown(e, data?.id)}>
                    <img className="menu" src={iconMenu} width="6" alt="menu" />
                </button>

                {showDropdown === data?.id && (
                    <div className="dropdown-menu" ref={DropdownRef}>
                        <ul className="dropdown-list">
                            <li className="dropdown-item">
                                <button
                                    type="button"
                                    className="item-btn"
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleAddToCampaign(data?.id);
                                    }}>
                                    <IoAdd size={18} />
                                    Add to Campaign
                                </button>
                            </li>
                            <li className="dropdown-item">
                                <button
                                    type="button"
                                    className="item-btn"
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleAddToList(data?.id);
                                    }}>
                                    <IoAdd size={18} />
                                    Add to List
                                </button>
                            </li>
                            <li className="dropdown-item">
                                <button
                                    type="button"
                                    className="item-btn"
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleRemoveFromJob(data?.id);
                                    }}>
                                    <IoTrashOutline size={18} />
                                    Remove from Job
                                </button>
                            </li>
                        </ul>
                    </div>
                )}
            </li>
        </ul>
    );

    // Table rows (clickable)
    const { product_rows, totalCount } = useMemo(() => {
        return {
            product_rows: leads_data?.data?.items?.map(data => ({
                id: data?.id,
                style: { cursor: 'pointer' },
                isDropdownOpen: showDropdown === data?.id,
                cells: [
                    <div className="lead-td-col" onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
                        <Field
                            type="checkbox"
                            noMargin
                            name={data?.id}
                            value={selectedLeads.includes(data?.id)}
                            onChange={({ target: { value } }) => handleSelectLead(data?.id, value)}
                            label={
                                <div className="flex-content" onClick={() => navigate(`/lead-preview/${data?.id}`)}>
                                    <div className="avatar-wrap">
                                        <img
                                            src={data.profile_photo || dummyImg}
                                            alt={data.name || 'Avatar'}
                                            className="avatar"
                                            onError={e => (e.currentTarget.src = dummyImg)}
                                        />
                                    </div>
                                    <div className="text-wrap">
                                        <span className="name">
                                            {`${data?.first_name || ''} ${capitalize(data?.last_name) || ''}` || '-'}
                                        </span>
                                    </div>
                                </div>
                            }
                        />
                    </div>,
                    <div className="badge-col" style={{ display: 'flex' }}>
                        {Status(data) || '-'}
                    </div>,
                    formatDate(data?.created_at),
                    data?.company_name || '-',
                    <span className="tag-text" style={{ color: '#0DA06A' }}>
                        {data?.title || '-'}
                    </span>,
                    data?.email || '-',
                    ActionBtn({ id: data?.id }),
                ],
            })),
            totalCount: leads_data?.data?.totalItems,
        };
    }, [leads_data, showDropdown, selectedLeads]);


    const handleAddToCampaign = (leadId) => {
        setShowDropdown(null);
        setAddToCampaignModal({ open: true, leadId });
        setSelectedCampaign(null);
        setPreviewData(null);
        fetchCampaigns();
    };

    const handleAddToList = (leadId) => {
        setShowDropdown(null);
        setAddToListModal({ open: true, leadId });
        setSelectedList(null);
        setPreviewData(null);
        fetchAllLists();
    };

    const handleBulkAddToList = () => {
        setShowBulkDropdown(false);
        setAddToListModal({ open: true, leadId: selectedLeads });
        setSelectedList(null);
        setPreviewData(null);
        fetchAllLists();
    };

    const handleRemoveFromJob = async (leadId) => {
        if (!leadId) return;

        setShowDropdown(null);
        setRemoveLoading(true);
        try {
            const payload = {
                jobId: Number(id),
                leadId: Array.isArray(leadId) ? leadId : [leadId],
            };

            const response = await jobService.removeLeadsFromJob(payload);

            if (response?.success) {
                Toast({ type: 'success', message: response?.message || 'Lead removed from job successfully' });
                setSearchQuery(prev => ({ ...prev, _refresh: Date.now() }));
                refetch();
            }
        } catch (error) {
            Toast({ type: 'error', message: error.message || 'Failed to remove lead from job' });
        } finally {
            setRemoveLoading(false);
        }
    };

    const handleBulkRemoveFromJob = async () => {
        if (selectedLeads.length === 0) {
            Toast({ type: 'error', message: 'No leads selected' });
            return;
        }

        setShowBulkDropdown(false);
        setRemoveLoading(true);

        try {
            const payload = {
                jobId: Number(id),
                leadId: selectedLeads,
            };

            const response = await jobService.removeLeadsFromJob(payload);

            if (response?.success) {
                Toast({
                    type: 'success',
                    message: `${selectedLeads.length} lead${selectedLeads.length > 1 ? 's' : ''} removed from job successfully`
                });

                setSelectedLeads([]);
                setTotalSelectedCount(0);
                setIsSelectAllMode(false);
                setSearchQuery(prev => ({ ...prev, _refresh: Date.now() }));
                refetch();
            }
        } catch (error) {
            Toast({ type: 'error', message: error.message || 'Failed to remove leads from job' });
        } finally {
            setRemoveLoading(false);
        }
    };

    const fetchTags = async () => {
        try {
            const res = await linkedinService.getTags();
            if (res?.success && res?.data) {
                const formattedTags = res.data.map(tag => ({
                    label: tag?.name,
                    value: tag?.id,
                }));
                setTags(formattedTags);
            }
        } catch (error) {
            Toast({ type: 'error', message: error.message || 'Failed to fetch tags' });
        }
    };

    const fetchAllLists = async () => {
        try {
            const res = await linkedinService.getAllLists();
            if (res?.success && res?.data) {
                const formattedLists = res.data.map(list => ({
                    label: list?.name,
                    value: list?.id,
                }));
                setLists(formattedLists);
            }
        } catch (error) {
            Toast({ type: 'error', message: error.message || 'Failed to fetch lists' });
        }
    };

    const fetchCampaigns = async () => {
        try {
            const res = await linkedinService.getAllCampaigns();
            if (res?.success && res?.data) {
                const formattedCampaigns = res.data.map(campaign => ({
                    label: campaign?.name,
                    value: campaign?.id?.toString(),
                    campaign_id: campaign?.id,
                    status: campaign?.status,
                }));

                // const filteredCampaigns = formattedCampaigns.filter(
                //     campaign => campaign.status === 'draft' || campaign.status === 'running',
                // );

                setCampaigns(formattedCampaigns);
            }
        } catch (error) {
            Toast({ type: 'error', message: error.message || 'Failed to fetch campaigns' });
        }
    };

    useEffect(() => {
        fetchTags();
        fetchAllLists();
        fetchCampaigns();
    }, []);

    const handleListSelection = async (listId) => {
        setSelectedList(listId);
        setPreviewData(null);

        if (!listId) return;

        if (!Array.isArray(addToListModal.leadId) || addToListModal.leadId.length <= 1) {
            return;
        }

        setPreviewLoading(true);
        try {
            const response = await linkedinService.getSublistUserLeadsPreview({
                sublistId: listId,
                leadId: addToListModal.leadId,
            });

            setPreviewData(response?.data || null);
        } catch (error) {
            Toast({ type: 'error', message: error?.message || 'Failed to load preview' });
            setPreviewData(null);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleAddLeadToList = async () => {
        if (!selectedList) {
            Toast({ type: 'error', message: 'Please select a list' });
            return;
        }

        setAddToListLoading(true);
        try {
            const leadIds = Array.isArray(addToListModal.leadId) ? addToListModal.leadId : [addToListModal.leadId];

            const res = await linkedinService.addLeadstoSublist({ userLeadId: leadIds, listId: selectedList });

            const leadCount = previewData?.uniqueLeads || leadIds.length;
            const successMessage =
                leadCount > 0
                    ? `${leadCount} ${leadCount === 1 ? 'lead' : 'leads'} added to ${res?.data?.sublistName}`
                    : '1 Lead added to list successfully';

            Toast({ type: 'success', message: successMessage });
            setAddToListModal({ open: false, leadId: null });
            setSelectedList(null);
            setPreviewData(null);
            refetch();
        }
        catch (error) {
            Toast({ type: 'error', message: error.message || 'Failed to add leads to list' });
        } finally {
            setAddToListLoading(false);
        }
    };

    const handleCampaignSelection = async campaignId => {
        setSelectedCampaign(campaignId);
        setPreviewData(null);

        if (!campaignId) return;

        const selectedCampaignData = campaigns.find(c => c.value === campaignId);
        if (!selectedCampaignData) return;

        // Show preview only for bulk operations
        if (Array.isArray(addToCampaignModal.leadId) && addToCampaignModal.leadId.length > 1) {
            setPreviewLoading(true);
            try {
                const response = await campaignService.getUserLeadsPreview({
                    campaignId: selectedCampaignData.campaign_id,
                    leadId: addToCampaignModal.leadId,
                });

                if (response?.success && response?.data) {
                    setPreviewData(response.data);
                }
            } catch (error) {
                Toast({ type: 'error', message: error.message || 'Failed to load preview' });
                setPreviewData(null);
            } finally {
                setPreviewLoading(false);
            }
        }
    };

    const handleAddLeadToCampaign = async () => {
        if (!selectedCampaign) {
            Toast({ type: 'error', message: 'Please select a campaign' });
            return;
        }

        const selectedCampaignData = campaigns.find(c => c.value === selectedCampaign);
        if (!selectedCampaignData) {
            Toast({ type: 'error', message: 'Campaign not found' });
            return;
        }

        setAddToCampaignLoading(true);
        try {
            const isBulk = Array.isArray(addToCampaignModal.leadId);
            const leadIds = isBulk ? addToCampaignModal.leadId : [addToCampaignModal.leadId];

            const payload = {
                leadId: leadIds,
                campaignId: selectedCampaignData.campaign_id,
                name: selectedCampaignData.label,
            };

            const response = await campaignService.addUserLeadToCampaign(payload);

            const addedCount = previewData?.uniqueLeads || leadIds.length;
            const successMessage = isBulk
                ? `${addedCount} ${addedCount === 1 ? 'lead' : 'leads'} added to ${response?.data?.campaignName || selectedCampaignData.label}`
                : `1 Lead added to ${response?.data?.campaignName || selectedCampaignData.label} successfully`;

            Toast({ type: 'success', message: successMessage });
            setAddToCampaignModal({ open: false, leadId: null });
            setSelectedCampaign(null);
            setPreviewData(null);

            if (isBulk) {
                setSelectedLeads([]);
                setTotalSelectedCount(0);
                setIsSelectAllMode(false);
            }
            refetch();

        } catch (error) {
            Toast({ type: 'error', message: error.message || 'Failed to add lead to campaign' });
        } finally {
            setAddToCampaignLoading(false);
        }
    };

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = event => {
            if (DropdownRef.current && !DropdownRef.current.contains(event.target)) {
                setShowDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showDropdown]);

    // Close Quick Actions and Filters on outside click
    useEffect(() => {
        const handleDocumentClick = e => {
            if (quickRef.current && !quickRef.current.contains(e.target)) {
                setQuickActive(false);
            }

            if (
                filtersRef.current &&
                !filtersRef.current.contains(e.target) &&
                (!filtersSidebarRef.current || !filtersSidebarRef.current.contains(e.target))
            ) {
                setOpenFilters(false);
            }

            if (bulkDropdownRef.current && !bulkDropdownRef.current.contains(e.target)) {
                setShowBulkDropdown(false);
            }
        };

        document.addEventListener('click', handleDocumentClick);
        return () => document.removeEventListener('click', handleDocumentClick);
    }, []);

    // Listen for edit icon click to close Quick Actions dropdown
    useEffect(() => {
        const handleCloseQuickActions = () => {
            setQuickActive(false);
        };
        const handleCloseFilters = () => {
            setOpenFilters(false);
        };
        window.addEventListener('close-quick-actions', handleCloseQuickActions);
        window.addEventListener('close-filters', handleCloseFilters);
        return () => {
            window.removeEventListener('close-quick-actions', handleCloseQuickActions);
            window.removeEventListener('close-filters', handleCloseFilters);
        };
    }, []);

    return (
        <StyledSingleLeads>
            <Form
                form={form}
                onSubmit={values => handleSearchNow(values.search, setSearchQuery)}>
                <div className="filter-wrap single-lead-filter" ref={filtersRef}>
                    <Form.Item
                        className="input-search"
                        colorBg
                        inputSm
                        noMargin
                        type="search"
                        rounded
                        name="search"
                        placeholder="Search Lead"
                        onChange={e => handleDebouncedSearch(e.target.value, form, debounceRef, setSearchQuery)}
                    >
                        <Field />
                    </Form.Item>
                    <div className="filter-box">
                        <button
                            type="button"
                            className="btn-filter"
                            onClick={() => setOpenFilters(!openFilters)}
                        >
                            <FaFilter className="icon" size={20} />
                        </button>
                    </div>
                    <Button variant="darkBlue" rounded width="150px">
                        Search
                    </Button>
                </div>
            </Form>

            <div className="table-area single-list-leads-table">
                <div
                    ref={filtersSidebarRef}
                    className={openFilters ? 'filters-holder active' : 'filters-holder'}>
                    <Filters
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        campaigns={campaigns}
                        tags={tags}
                        lists={lists}
                        hideListFilter
                        filters={[
                            'Lead Status',
                            'Contacted Status',
                            'Lead Source',
                            'Campaign',
                            'Tags',
                            'Date Created',
                            'Company Name',
                            'Job Title',
                            'Email',
                            'LinkedIn Profile',
                            'Language',
                            'Location',
                            'Industry',
                            'Headline',
                            'Open to Work',
                            'Is Hiring',
                        ]}
                    />
                </div>
                <TableLayout
                    noStyle
                    onChangeFilters={filters => {
                        setSearchQuery(_ => ({
                            ..._,
                            ...filters,
                        }));
                    }}
                    currentPage={searchQuery?.page}
                    pageSize={searchQuery?.itemsPerPage}
                    totalCount={totalCount}>
                    <Table rowsData={product_rows} columnNames={columnNames} noPadding />
                </TableLayout>
            </div>

            {/* Add to Campaign Modal */}
            <CenterModal
                title="Add to Campaign"
                open={addToCampaignModal.open}
                setOpen={isOpen => {
                    setAddToCampaignModal({ open: isOpen, leadId: null });
                    setSelectedCampaign(null);
                    setPreviewData(null);
                }}
                width="600">
                <div style={{ padding: '20px' }}>
                    <AddToModalContent
                        label="Select Campaign"
                        placeholder="Select a campaign"
                        options={campaigns}
                        selectedValue={selectedCampaign}
                        setSelectedValue={handleCampaignSelection}
                        loading={addToCampaignLoading}
                        onCancel={() => {
                            setAddToCampaignModal({ open: false, leadId: null });
                            setSelectedCampaign(null);
                            setPreviewData(null);
                        }}
                        onSubmit={handleAddLeadToCampaign}
                        showPreview={Array.isArray(addToCampaignModal.leadId) && addToCampaignModal.leadId.length > 1}
                        previewLoading={previewLoading}
                        previewData={previewData}
                    />
                </div>
            </CenterModal>

            {/* Add to List Modal */}
            <CenterModal
                title="Add to List"
                open={addToListModal.open}
                setOpen={isOpen => {
                    setAddToListModal({ open: isOpen, leadId: null });
                    setSelectedList(null);
                    setPreviewData(null);
                }}
                width="600">
                <div style={{ padding: '20px' }}>
                    <AddToModalContent
                        label="Select List"
                        placeholder="Select a list"
                        options={lists}
                        selectedValue={selectedList}
                        setSelectedValue={handleListSelection}
                        loading={addToListLoading}
                        onCancel={() => {
                            setAddToListModal({ open: false, leadId: null });
                            setSelectedList(null);
                            setPreviewData(null);
                        }}
                        onSubmit={handleAddLeadToList}
                        showPreview={Array.isArray(addToListModal.leadId) && addToListModal.leadId.length > 1}
                        previewLoading={previewLoading}
                        previewData={previewData}
                    />
                </div>
            </CenterModal>
        </StyledSingleLeads>
    );
};

export default SingleJobLeadsDetails;
